import { country_state_enum } from '@predium/enums';

/**
 * Maps a UK postcode "area" (the leading letters of the outward code, e.g. "EH" in "EH1 2AB")
 * to its ITL1 region. It is intentionally approximate — some areas straddle region boundaries
 * and are assigned to their predominant region — which is fine for its two consumers:
 *
 * - Detecting Scotland during EPC creation, before the location service has resolved the region
 *   (Scotland's EPC class is CO2-based, so the Energy Performance Asset Rating is not editable
 *   there — BLU-2940).
 * - The local AWS Location mock, which uses it to return a plausible, deterministic country_state
 *   for any UK address.
 *
 * For the UK the country_state only drives display/metadata (the climate region is always
 * UK_NATIONAL, and CRREM keys off the postcode→NUTS map, not the state), so approximate
 * boundaries are acceptable.
 */
export const UK_POSTCODE_AREA_TO_REGION: Record<string, country_state_enum> = {
  // Scotland
  AB: country_state_enum.UK_SCOTLAND,
  DD: country_state_enum.UK_SCOTLAND,
  DG: country_state_enum.UK_SCOTLAND,
  EH: country_state_enum.UK_SCOTLAND,
  FK: country_state_enum.UK_SCOTLAND,
  G: country_state_enum.UK_SCOTLAND,
  HS: country_state_enum.UK_SCOTLAND,
  IV: country_state_enum.UK_SCOTLAND,
  KA: country_state_enum.UK_SCOTLAND,
  KW: country_state_enum.UK_SCOTLAND,
  KY: country_state_enum.UK_SCOTLAND,
  ML: country_state_enum.UK_SCOTLAND,
  PA: country_state_enum.UK_SCOTLAND,
  PH: country_state_enum.UK_SCOTLAND,
  TD: country_state_enum.UK_SCOTLAND,
  ZE: country_state_enum.UK_SCOTLAND,
  // Wales
  CF: country_state_enum.UK_WALES,
  LD: country_state_enum.UK_WALES,
  LL: country_state_enum.UK_WALES,
  NP: country_state_enum.UK_WALES,
  SA: country_state_enum.UK_WALES,
  // Northern Ireland
  BT: country_state_enum.UK_NORTHERN_IRELAND,
  // North East England
  DH: country_state_enum.UK_NORTH_EAST,
  DL: country_state_enum.UK_NORTH_EAST,
  NE: country_state_enum.UK_NORTH_EAST,
  SR: country_state_enum.UK_NORTH_EAST,
  TS: country_state_enum.UK_NORTH_EAST,
  // North West England
  BB: country_state_enum.UK_NORTH_WEST,
  BL: country_state_enum.UK_NORTH_WEST,
  CA: country_state_enum.UK_NORTH_WEST,
  CH: country_state_enum.UK_NORTH_WEST,
  CW: country_state_enum.UK_NORTH_WEST,
  FY: country_state_enum.UK_NORTH_WEST,
  L: country_state_enum.UK_NORTH_WEST,
  LA: country_state_enum.UK_NORTH_WEST,
  M: country_state_enum.UK_NORTH_WEST,
  OL: country_state_enum.UK_NORTH_WEST,
  PR: country_state_enum.UK_NORTH_WEST,
  SK: country_state_enum.UK_NORTH_WEST,
  WA: country_state_enum.UK_NORTH_WEST,
  WN: country_state_enum.UK_NORTH_WEST,
  // Yorkshire and the Humber
  BD: country_state_enum.UK_YORKSHIRE_AND_THE_HUMBER,
  DN: country_state_enum.UK_YORKSHIRE_AND_THE_HUMBER,
  HD: country_state_enum.UK_YORKSHIRE_AND_THE_HUMBER,
  HG: country_state_enum.UK_YORKSHIRE_AND_THE_HUMBER,
  HU: country_state_enum.UK_YORKSHIRE_AND_THE_HUMBER,
  HX: country_state_enum.UK_YORKSHIRE_AND_THE_HUMBER,
  LS: country_state_enum.UK_YORKSHIRE_AND_THE_HUMBER,
  S: country_state_enum.UK_YORKSHIRE_AND_THE_HUMBER,
  WF: country_state_enum.UK_YORKSHIRE_AND_THE_HUMBER,
  YO: country_state_enum.UK_YORKSHIRE_AND_THE_HUMBER,
  // East Midlands
  DE: country_state_enum.UK_EAST_MIDLANDS,
  LE: country_state_enum.UK_EAST_MIDLANDS,
  LN: country_state_enum.UK_EAST_MIDLANDS,
  NG: country_state_enum.UK_EAST_MIDLANDS,
  NN: country_state_enum.UK_EAST_MIDLANDS,
  // West Midlands
  B: country_state_enum.UK_WEST_MIDLANDS,
  CV: country_state_enum.UK_WEST_MIDLANDS,
  DY: country_state_enum.UK_WEST_MIDLANDS,
  HR: country_state_enum.UK_WEST_MIDLANDS,
  ST: country_state_enum.UK_WEST_MIDLANDS,
  SY: country_state_enum.UK_WEST_MIDLANDS,
  TF: country_state_enum.UK_WEST_MIDLANDS,
  WR: country_state_enum.UK_WEST_MIDLANDS,
  WS: country_state_enum.UK_WEST_MIDLANDS,
  WV: country_state_enum.UK_WEST_MIDLANDS,
  // East of England
  AL: country_state_enum.UK_EAST_OF_ENGLAND,
  CB: country_state_enum.UK_EAST_OF_ENGLAND,
  CM: country_state_enum.UK_EAST_OF_ENGLAND,
  CO: country_state_enum.UK_EAST_OF_ENGLAND,
  IP: country_state_enum.UK_EAST_OF_ENGLAND,
  LU: country_state_enum.UK_EAST_OF_ENGLAND,
  NR: country_state_enum.UK_EAST_OF_ENGLAND,
  PE: country_state_enum.UK_EAST_OF_ENGLAND,
  SG: country_state_enum.UK_EAST_OF_ENGLAND,
  SS: country_state_enum.UK_EAST_OF_ENGLAND,
  // London
  BR: country_state_enum.UK_LONDON,
  CR: country_state_enum.UK_LONDON,
  DA: country_state_enum.UK_LONDON,
  E: country_state_enum.UK_LONDON,
  EC: country_state_enum.UK_LONDON,
  EN: country_state_enum.UK_LONDON,
  HA: country_state_enum.UK_LONDON,
  IG: country_state_enum.UK_LONDON,
  KT: country_state_enum.UK_LONDON,
  N: country_state_enum.UK_LONDON,
  NW: country_state_enum.UK_LONDON,
  RM: country_state_enum.UK_LONDON,
  SE: country_state_enum.UK_LONDON,
  SM: country_state_enum.UK_LONDON,
  SW: country_state_enum.UK_LONDON,
  TW: country_state_enum.UK_LONDON,
  UB: country_state_enum.UK_LONDON,
  W: country_state_enum.UK_LONDON,
  WC: country_state_enum.UK_LONDON,
  WD: country_state_enum.UK_LONDON,
  // South East England
  BN: country_state_enum.UK_SOUTH_EAST,
  CT: country_state_enum.UK_SOUTH_EAST,
  GU: country_state_enum.UK_SOUTH_EAST,
  HP: country_state_enum.UK_SOUTH_EAST,
  ME: country_state_enum.UK_SOUTH_EAST,
  MK: country_state_enum.UK_SOUTH_EAST,
  OX: country_state_enum.UK_SOUTH_EAST,
  PO: country_state_enum.UK_SOUTH_EAST,
  RG: country_state_enum.UK_SOUTH_EAST,
  RH: country_state_enum.UK_SOUTH_EAST,
  SL: country_state_enum.UK_SOUTH_EAST,
  SO: country_state_enum.UK_SOUTH_EAST,
  TN: country_state_enum.UK_SOUTH_EAST,
  // South West England
  BA: country_state_enum.UK_SOUTH_WEST,
  BH: country_state_enum.UK_SOUTH_WEST,
  BS: country_state_enum.UK_SOUTH_WEST,
  DT: country_state_enum.UK_SOUTH_WEST,
  EX: country_state_enum.UK_SOUTH_WEST,
  GL: country_state_enum.UK_SOUTH_WEST,
  PL: country_state_enum.UK_SOUTH_WEST,
  SN: country_state_enum.UK_SOUTH_WEST,
  SP: country_state_enum.UK_SOUTH_WEST,
  TA: country_state_enum.UK_SOUTH_WEST,
  TQ: country_state_enum.UK_SOUTH_WEST,
  TR: country_state_enum.UK_SOUTH_WEST,
};

/** Extracts the postcode "area" — the leading letters of the outward code, e.g. "EH" in "EH7 5PS". */
const getUkPostcodeArea = (postalCode: string): string | undefined =>
  postalCode
    .replace(/\s/g, '')
    .toUpperCase()
    .match(/^[A-Z]+/)?.[0];

/**
 * Resolves a UK postcode to its (approximate) ITL1 region via the postcode area.
 * Returns null for empty input or unrecognised areas (e.g. Crown dependencies) so callers
 * decide their own fallback.
 */
export const getUkRegionForPostcode = (postalCode: string | null | undefined): country_state_enum | null => {
  if (!postalCode) {
    return null;
  }
  const area = getUkPostcodeArea(postalCode);
  return (area && UK_POSTCODE_AREA_TO_REGION[area]) || null;
};

/**
 * Whether a UK postcode belongs to a Scottish postcode area. Returns false for empty/unknown
 * postcodes so callers default to non-Scottish behaviour.
 */
export const isScottishPostcode = (postalCode: string | null | undefined): boolean =>
  getUkRegionForPostcode(postalCode) === country_state_enum.UK_SCOTLAND;
